import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomChart from "../Charts";
import styles from "../../pages/Welcome/style.module.scss";
import { intentLabelToStackedBarRgba } from "../../constants/dashboardIntentColors";

const tickColor = (theme) => (theme === "dark" ? "white" : "black");

const gridColor = (theme) =>
  theme === "dark"
    ? "rgba(255, 255, 255, 0.1)"
    : "rgba(0, 0, 0, 0.1)";

/**
 * KPIStackedBarChart — stacked bar for Welcome KPI data.
 * Default: vertical bars (topics on x-axis), matching Dashboard "Topics Sentiment" template.
 * Pass horizontal=true for legacy horizontal layout (categories on y-axis).
 */
const KPIStackedBarChart = ({
  data,
  title,
  theme,
  horizontal = false,
  /** When true, recolor each dataset using dashboard console intent hex (metric 12). */
  applyDashboardIntentColors = false,
  /** (index, label) — when set, bars (vertical) or categories (horizontal) are clickable. */
  onCategoryClick,
}) => {
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const categoryClickRef = useRef(onCategoryClick);
  categoryClickRef.current = onCategoryClick;
  const enableCategoryClick = typeof onCategoryClick === "function";

  const options = useMemo(() => {
    const tc = tickColor(theme);
    const gc = gridColor(theme);
    const legendPlugin = {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          color: tc,
          usePointStyle: true,
          padding: 16,
        },
      },
      datalabels: {
        color: "white",
        font: {
          size: 11,
        },
        formatter: (value) => value,
        display: horizontal,
        anchor: "center",
        align: "center",
      },
    };
    const layoutPadding = horizontal
      ? { left: 10, right: 10, top: 10, bottom: 10 }
      : { left: 8, right: 8, top: 8, bottom: 36 };

    if (horizontal) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              color: tc,
            },
            min: 0,
            max: 100,
          },
          y: {
            stacked: true,
            grid: {
              display: true,
              drawBorder: false,
              color: gc,
            },
            ticks: {
              color: tc,
              callback: function (value) {
                const label = this.getLabelForValue(value);
                const maxLength = 15;
                if (label.length > maxLength) {
                  return `${label.substring(0, maxLength)}..`;
                }
                return label;
              },
            },
          },
        },
        plugins: legendPlugin,
        backgroundColor: "transparent",
        layout: {
          padding: layoutPadding,
        },
      };
    }

    const verticalInteraction =
      !horizontal && enableCategoryClick
        ? {
            onClick: (evt, elements, chart) => {
              const fn = categoryClickRef.current;
              if (!fn || !elements?.length) return;
              const idx = elements[0].index;
              const labels = chart?.data?.labels;
              const label =
                labels != null && labels[idx] !== undefined
                  ? labels[idx]
                  : null;
              fn(idx, label);
            },
            onHover: (evt, elements) => {
              if (evt?.native?.target) {
                evt.native.target.style.cursor =
                  elements?.length > 0 ? "pointer" : "default";
              }
            },
          }
        : {};

    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "x",
      ...verticalInteraction,
      datasets: {
        bar: {
          categoryPercentage: 0.42,
          barPercentage: 1,
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            color: tc,
            maxRotation: 45,
            minRotation: 45,
            autoSkip: false,
            font: { size: 11 },
          },
        },
        y: {
          stacked: true,
          min: 0,
          max: 100,
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            color: tc,
            stepSize: 20,
          },
        },
      },
      plugins: legendPlugin,
      backgroundColor: "transparent",
      layout: {
        padding: layoutPadding,
      },
    };
  }, [theme, horizontal, enableCategoryClick]);

  useEffect(() => {
    try {
      const processedData = data.datasets
        ? data
        : {
            labels: data.labels || [],
            datasets: data.datasets || [
              {
                label: "Dataset 1",
                data: data.values || [],
                backgroundColor: "rgba(54, 162, 235, 0.8)",
                borderColor: "rgba(54, 162, 235, 1)",
                borderWidth: 1,
              },
            ],
          };

      if (processedData.datasets) {
        processedData.datasets.forEach((dataset) => {
          if (!dataset.stack) {
            dataset.stack = "stack0";
          }
          if (applyDashboardIntentColors && dataset.label != null) {
            dataset.backgroundColor = intentLabelToStackedBarRgba(
              dataset.label,
              0.8,
            );
          }
        });
      }

      setChartData(processedData);
      setError(null);
    } catch (err) {
      console.error("Error processing chart data:", err);
      setError("Failed to process chart data");
    }
  }, [data, applyDashboardIntentColors]);

  const chartRevision = useMemo(
    () => ({ data: chartData, enableCategoryClick }),
    [chartData, enableCategoryClick],
  );

  if (error) {
    return <div className={styles.chartError}>Error: {error}</div>;
  }

  if (!chartData) {
    return <div className={styles.chartLoading}>Loading chart...</div>;
  }

  return (
    <div className={styles.chartContainer}>
      <CustomChart
        type="bar"
        data={chartData}
        options={options}
        theme={theme}
        dependency={chartRevision}
      />
    </div>
  );
};

export default KPIStackedBarChart;
